/**
 * Mount-once-per-tab subscription that turns every Hatif webhook into a
 * silent cache update for any cached conversation in the user's company.
 *
 * Why: the message page used to subscribe per-active-conversation, which
 * means a webhook for a phone the user wasn't currently viewing would
 * land in the audit log but nothing in React Query knew about it —
 * navigating back to that phone showed stale data until the next manual
 * refetch (with a spinner). Pivoting to one company-wide sub + a router
 * fixes that without changing the UX cost for the active page: same
 * incremental merge, same no-loading-flash, just applied to every
 * cached subtree the event affects.
 *
 * Algorithm per incoming row:
 *   1. extractRoutingKey(row) → 'conversation' for whatsapp w/ a
 *      conversation_id; 'none' for calls or detached events. 'none' →
 *      drop (no cache key to match).
 *   2. Walk every cached `['hatif-conversation', companyId, _, _, 'lookup']`
 *      entry. For each, the stored value is `HatifConversation | null` —
 *      compare its `.id` to the routing key.
 *   3a. For every non-null match, derive the corresponding `'timeline'`
 *       query key and call `syncTimelineCache`. The merge engine is
 *       single-flight per conversation, so concurrent hook + router
 *       calls collapse into one drain loop without double-prepending.
 *   3b. When no non-null match was found, fall through to
 *       `resolveNewConversation`. This covers the "phone was visited but
 *       no Voxa conversation existed at the time" case: we fetch the
 *       conversation by id, match by phone, and silently populate both
 *       caches. Active page transitions to the live conversation UI
 *       without a spinner; inactive pages have fresh cache for next
 *       visit.
 *
 * Mount the hook ONCE inside the authed shell (after `companyId` is
 * available) so the channel lives across page navigation. Unmounting on
 * logout / route swap tears down the channel cleanly.
 */
import type { HatifConversation } from '@dallali/notifications';
import type { QueryKey } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { extractRoutingKey } from '@/lib/hatif/extractRoutingKey';
import { resolveNewConversation } from '@/lib/hatif/resolveNewConversation';
import { syncTimelineCache } from '@/lib/hatif/syncTimelineCache';
import { subscribeHatifCompanyEvents } from '@/repositories/hatif';

import { useAuth } from './useAuth';
import { useCompanyHatifCredentials } from './useCompanyHatifCredentials';

export function useHatifWebhookCacheRouter(): void {
  const { companyId } = useAuth();
  const queryClient = useQueryClient();
  const hatifStatus = useCompanyHatifCredentials(companyId);
  // Primitive derived from the query so the effect only re-runs when the
  // configured flag actually flips, not on every React Query state update.
  // Note: the underlying RPC is super-admin-gated, and the hook mirrors that
  // gate client-side (`enabled: ... && isSuperAdmin`) — for non-admin users
  // the query stays idle (never fires) and this stays false, which silently
  // disables Phase 2 (acceptable: Phase 1 and all normal queries still work).
  const hatifConfigured = hatifStatus.data?.configured ?? false;

  useEffect(() => {
    if (!companyId) return;

    return subscribeHatifCompanyEvents({
      companyId,
      onInsert: (row) => {
        const key = extractRoutingKey(row);
        if (key.kind !== 'conversation') return;
        const { conversationId } = key;

        // Predicate scope: same company, the 'lookup' tier of the
        // hatif-conversation cache. Used by both phases below.
        const lookups = queryClient.getQueriesData<HatifConversation | null>({
          predicate: ({ queryKey }) => {
            return (
              Array.isArray(queryKey) &&
              queryKey[0] === 'hatif-conversation' &&
              queryKey[1] === companyId &&
              queryKey[4] === 'lookup'
            );
          },
        });

        // Phase 1: every cached conversation lookup whose resolved id
        // matches this row — incremental timeline merge per match.
        let matchedAny = false;
        for (const [lookupKey, conversation] of lookups) {
          if (!conversation || conversation.id !== conversationId) continue;
          matchedAny = true;
          // Lookup key shape: ['hatif-conversation', companyId, channelId, phone, 'lookup']
          // Timeline key shape: ['hatif-conversation', companyId, channelId, phone, 'timeline', conversationId]
          const [, , channelId, phone] = lookupKey as readonly unknown[];
          const timelineQueryKey: QueryKey = [
            'hatif-conversation',
            companyId,
            channelId,
            phone,
            'timeline',
            conversationId,
          ];
          void syncTimelineCache({
            queryClient,
            companyId,
            conversationId,
            timelineQueryKey,
          });
        }

        if (matchedAny) return;

        // Phase 2: no existing match. Skip if the company has no Hatif
        // credentials — calling the Voxa API would return 400 and produce
        // a browser-level console error that E2E guards treat as a failure.
        if (!hatifConfigured) return;

        // If any cached lookups are `null` (we previously found no Voxa
        // conversation for some phone), the resolver fetches the
        // conversation, matches by phone, and silently populates the cache
        // so neither the active page nor a later visit hits a loading state.
        void resolveNewConversation({
          queryClient,
          companyId,
          conversationId,
          lookups,
        });
      },
    });
  }, [companyId, queryClient, hatifConfigured]);
}
